import { Button, Card, CardBody, Input, Modal, ModalBody, ModalContent, ModalHeader, Textarea, useDisclosure } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { AiOutlineDelete, AiOutlineEdit, AiOutlinePlus } from "react-icons/ai";
import { Block } from "../types/types";
import Tree from "./jsonSchemaEditor";
import { JSONSchema7 } from "json-schema";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";


// Block Component
// eslint-disable-next-line @typescript-eslint/no-redeclare
const BlockComponent: React.FC<{
    block: Block;
    onUpdate: (id: string, updatedBlock: Block) => void;
    onDelete: (id: string) => void;
}> = ({ block, onUpdate, onDelete }) => {

    const [schema, setSchema] = useState<JSONSchema7>(block.input?.schema || {})
    const [template, setTemplate] = useState<string>(block.output?.template || "")

    const saveSchemaChange = () => {
        onUpdate(block.id, {
            ...block,
            input: { query: block.input?.query || "", schema: schema },
            output: { template: template, data: {} }
        });
    };

    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    return (
        <>

            <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="5xl" >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1 text-lg ">Edit {block.input?.schema.title}</ModalHeader>

                            <ModalBody className="flex flex-col" >
                                <div className="flex flex-col ">
                                    <div className="text-md font-bold">Extract </div>
                                    <div className="flex max-h-[600px] overflow-y-scroll w-full">
                                        <Tree onSchemaChange={(schema) => setSchema(schema)} schema={block.input?.schema} />
                                    </div>
                                </div>
                                <div className="flex flex-col">
                                    <div className="text-md font-bold">Output Template</div>
                                    <div className="text-xs">Use markdown/handlebar templates</div>
                                    <div className="flex mt-2">
                                        <Textarea
                                            className="Code-editor"
                                            name="template"
                                            onChange={(e) => setTemplate(e.target.value)}
                                            value={template}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-row gap-4 justify-end">
                                    <Button color="danger" variant="light" onPress={onClose}>Cancel</Button>

                                    <Button color="primary" onPress={() => { saveSchemaChange(); onClose(); }}>Save</Button>
                                </div>
                            </ModalBody>
                        </>
                    )
                    }
                </ModalContent>
            </Modal>

            <Card className="mt-2" >
                <CardBody>
                    <div className="flex flex-row gap-1 justify-between" >

                        {/* <Input
                        type="text"
                        name="schema"
                        readOnly
                        placeholder="Input Schema"
                        value={block.input?.schema.title}
                        onChange={handleInputChange}
                        variant="underlined"
                    /> */}
                        {block.input?.schema.title}
                        {/* <Textarea
                        name="output"
                        placeholder="Output template"
                        value={block.output?.template}
                        onChange={handleInputChange}
                        variant="bordered"

                    /> */}

                        <div className="flex flex-row gap-5" >
                            <button className="text-red-500" onClick={onOpen}>
                                <AiOutlineEdit size={18} />
                            </button>
                            <button className="text-red-500" onClick={() => onDelete(block.id)}>
                                <AiOutlineDelete size={18} />
                            </button>
                        </div>

                    </div>
                </CardBody>
            </Card>
        </>
    );
};

// BlockEditor Component
const BlockEditor: React.FC<{ baseBlocks: Block[], onUpdate: (blocks: Block[]) => void }> = ({ baseBlocks, onUpdate }) => {
    const [blocks, setBlocks] = useState<Block[]>(baseBlocks);

    // Add a new block
    const addBlock = () => {
        const ids = blocks.map(obj => Number(obj.id))
        if (ids.length === 0) ids.push(0);
        const maxId = Math.max(...ids);
        const newBlock: Block = {
            id: `${maxId + 1}`,

        };
        setBlocks([...blocks, newBlock]);
    };

    // Update a block
    const updateBlock = (id: string, updatedBlock: Block) => {
        setBlocks(blocks.map((block) => (block.id === id ? updatedBlock : block)));
    };

    // Delete a block
    const deleteBlock = (id: string) => {
        setBlocks(blocks.filter((block) => block.id !== id));
    };

    // Handle drag-and-drop
    const onDragEnd = (result: any) => {
        const { source, destination } = result;

        // If dropped outside a valid destination, do nothing
        if (!destination) return;

        // Reorder the blocks array
        const reorderedBlocks = Array.from(blocks);
        const [removed] = reorderedBlocks.splice(source.index, 1);
        reorderedBlocks.splice(destination.index, 0, removed);

        setBlocks(reorderedBlocks);
    };

    useEffect(() => {
        onUpdate(blocks)
    }, [blocks])

    return (
        <div style={styles.editorContainer}>
            <button className="text-red-500" onClick={addBlock}>
                <AiOutlinePlus size={18} />
            </button>
            {blocks ? (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="blocks">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {blocks.map((block, index) => (
                                    <Draggable key={block.id} draggableId={block.id.toString()} index={index}>
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                <BlockComponent
                                                    block={block}
                                                    onUpdate={updateBlock}
                                                    onDelete={deleteBlock}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            ) : <></>}
        </div>
    );
};

// Styles for the editor
const styles = {
    editorContainer: {
        padding: "20px",
    },
    blockContainer: {
        border: "1px solid #ccc",
        padding: "15px",
        margin: "10px 0",
        borderRadius: "8px",
    },
    input: {
        display: "block",
        width: "100%",
        padding: "8px",
        margin: "5px 0",
        fontSize: "16px",
    },
    textarea: {
        display: "block",
        width: "100%",
        height: "80px",
        padding: "8px",
        margin: "5px 0",
        fontSize: "16px",
    },

};

export default BlockEditor;